import React, { useState, useEffect } from "react";
import { Checkbox, Button } from "antd";
import {
  DownloadOutlined,
  VideoCameraOutlined,
  AudioOutlined,
  FileOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import { ICONS } from "../../../../assets/icons";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "file", label: "Files" },
  { key: "image", label: "Images" },
  { key: "video", label: "Video" },
  { key: "audio", label: "Audio" },
];

// Helper: Icon based on type
const getIconByType = (item) => {
  const type = item.attachment_type;
  const mime = item.mime_type || "";
  const fileName = item.file_name || "";
  const ext = fileName.split(".").pop().toLowerCase();

  if (type === "video") {
    return <VideoCameraOutlined className="text-2xl text-gray-500" />;
  }

  if (type === "audio") {
    return <AudioOutlined className="text-2xl text-gray-500" />;
  }

  if (mime === "application/pdf" || ext === "pdf") {
    return <FilePdfOutlined className="text-2xl text-red-500" />;
  }

  if (
    mime ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    mime === "application/vnd.ms-excel" ||
    ext === "xlsx" ||
    ext === "xls"
  ) {
    return <FileExcelOutlined className="text-2xl text-green-500" />;
  }

  return <FileOutlined className="text-2xl text-gray-500" />;
};

export default function AttachmentsManager({
  items,
  activeFilter = "all",
  onFilterChange,
  onDownloadItem,
  onDownloadItems,
}) {
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    // Remove selections that no longer exist
    setSelectedIds((ids) => ids.filter((id) => items.some((i) => i.id === id)));
  }, [items]);

  return (
    <div className="shadow p-4 flex flex-col h-full">
      {/* Filter buttons */}
      <div className="flex flex-wrap space-1.5 justify-between text-[12px] gap-y-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => onFilterChange(f.key)}
            className={
              `px-3 py-1 rounded-full transition ` +
              (activeFilter === f.key
                ? "bg-green-100 border border-green-100 text-green-800"
                : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-200")
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List or empty state */}
      <div className="flex-1 mt-4 space-y-2 overflow-y-auto w-full">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <FileOutlined className="text-4xl mb-2" />
            <span className="text-[12px]">No attachments available.</span>
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-2 transition-all duration-300 cursor-pointer rounded-lg hover:bg-gray-50"
            >
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={selectedIds.includes(item.id)}
                  onChange={(e) =>
                    setSelectedIds((sel) =>
                      e.target.checked
                        ? [...sel, item.id]
                        : sel.filter((id) => id !== item.id)
                    )
                  }
                />

                {/* Preview */}
                <div className="w-8 h-8 flex items-center shrink-1 justify-center rounded overflow-hidden">
                  {item.attachment_type === "image" ? (
                    <img
                      src={item.file_url}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    getIconByType(item)
                  )}
                </div>

                {/* File info */}
                <div>
                  <div className="text-sm truncate font-medium text-gray-800 max-w-[150px]">
                    {item.file_name}
                  </div>
                  <div className="text-xs text-gray-400">
                    {new Date(item.created_at).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Single download */}
              <Button
                type="text"
                shape="circle"
                icon={<DownloadOutlined />}
                onClick={() => onDownloadItem(item)}
              />
            </div>
          ))
        )}
      </div>

      {/* Bulk download */}
      <div className="mt-4 flex w-full justify-end">
        <button
          type="button"
          disabled={selectedIds.length === 0}
          onClick={() =>
            onDownloadItems(
              items.filter((item) => selectedIds.includes(item.id))
            )
          }
          className="bg-[#05805C] text-white w-full text-[14px] justify-center px-4 py-2 rounded-md flex items-center disabled:bg-gray-400 cursor-pointer"
        >
          <DownloadOutlined className="mr-2 text-lg" />
          Download all files ({selectedIds.length})
        </button>
      </div>
    </div>
  );
}
